import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import {
  DEFAULT_PAGE_SIZE,
  Loader,
  ColumnManager,
  AreYouSureModal,
} from "@tfg/ui"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { ProductFilters } from "~/components/ProductFilters"
import { ProductSearch } from "~/components/ProductSearch"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { CustomTable } from "~/components/table/CustomTable"
import {
  FindProductsCatalogRequestFiltersDto,
  FindProductsCatalogRequestSortsDto,
  useFindCatalogProducts,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { CatalogActionsMenu } from "~/pages/Brand/Products/List/Components/ProductsTables/catalog/CatalogActionsMenu"
import { ProductsCatalogSorts } from "~/pages/Brand/Products/List/Components/ProductsTables/catalog/ProductsCatalogSorts"
import { useCatalogTable } from "~/pages/Brand/Products/List/Components/ProductsTables/catalog/useCatalogTable"
import { ProductsListView } from "~/pages/Brand/Products/List/constants.ts"
import {
  useDeleteProductsMutation,
  useDownloadProductsCSVMutation,
} from "~/pages/Brand/Products/List/hooks"

export const CatalogProductsTable = () => {
  const view = ProductsListView.GENERAL

  const { t } = useTranslation(["products", "common"])

  const [
    isDeleteModalOpen,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columnManagerTranslations = useGetColumnManagerTranslations()
  const [filtersValue, setFiltersValue] = useSearchParamsFilters(
    z.custom<FindProductsCatalogRequestFiltersDto>()
  )

  const [pageCount, setPageCount] = useState<number>(0)

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }

  const [sorts, setSorts] = useState<FindProductsCatalogRequestSortsDto>({})
  const { data: productsData, isPending: isLoadingProducts } =
    useFindCatalogProducts({
      filters: filtersValue,
      sorts,
      pagination: {
        pageIndex: fetchDataOptions.pageIndex + 1,
        pageSize: fetchDataOptions.pageSize,
      },
    })

  useEffect(() => {
    if (productsData?.data.totalProducts === 0) {
      setPageCount(0)
    }
    if (productsData?.data.totalProducts) {
      setPageCount(Math.ceil(productsData?.data.totalProducts / pageSize))
    }
  }, [productsData?.data.totalProducts, pageSize])

  const products = useMemo(() => {
    return productsData?.data.products ?? []
  }, [productsData])

  const { mutate: exportAll, isPending: isProductExportLoading } =
    useDownloadProductsCSVMutation({
      filters: filtersValue,
    })

  const { mutate: deleteProducts, isPending: isProductsDeleteLoading } =
    useDeleteProductsMutation({
      filters: filtersValue,
      sorts,
      fetchDataOptions,
      onDeleteSettled: () => {
        setRowSelection({})
        closeDeleteModal()
      },
    })

  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const table = useCatalogTable({
    pagination,
    rowSelection,
    setPagination,
    setRowSelection,
    pageCount,
    products,
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [filtersValue])

  const selectedProductIds = useMemo(() => {
    return Object.keys(rowSelection).filter((row) => rowSelection[row])
  }, [rowSelection])

  return (
    <>
      <Stack>
        <Group>
          <ProductSearch
            filtersValue={filtersValue}
            setFiltersValue={setFiltersValue}
            isFetching={isLoadingProducts}
          />
          <ProductsCatalogSorts sorts={sorts} setSorts={setSorts} />
          <ProductFilters<FindProductsCatalogRequestFiltersDto>
            setFiltersValue={setFiltersValue}
            filtersValue={filtersValue}
            extraFilters={[
              {
                label: t("products:lists.placeholders.launchDate"),
                key: "incomingLaunchDate",
                options: [
                  {
                    value: "incoming",
                    label: t("products:lists.placeholders.incoming"),
                  },
                  {
                    value: "expired",
                    label: t("products:lists.placeholders.expired"),
                  },
                  {
                    value: "all",
                    label: t("products:lists.placeholders.allF"),
                  },
                ],
                type: "select",
              },
            ]}
          />
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <Group style={{ marginLeft: "auto" }}>
            <CatalogActionsMenu
              selectedProductIds={selectedProductIds}
              onProductExportClick={exportAll}
              isProductExportLoading={isProductExportLoading}
              onProductsDeleteClick={openDeleteModal}
              isProductsDeleteLoading={isProductsDeleteLoading}
            />
          </Group>
        </Group>
        {isLoadingProducts ? (
          <Loader fullPage />
        ) : (
          <CustomTable
            table={table}
            view={view}
            manualPaginationCount={productsData?.data.totalProducts || 0}
            stickyHeader
            layout="fixed"
            setOpenColumnManager={setIsColumnManagerOpen}
          />
        )}
      </Stack>
      <AreYouSureModal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={() => {
          if (selectedProductIds.length === 0) {
            return
          }
          deleteProducts({ data: { productIds: selectedProductIds } })
        }}
        areYouSureText={t("products:bulkActions.delete.modal.message")}
        confirmText={t("products:bulkActions.delete.modal.confirm")}
        cancelText={t("products:bulkActions.delete.modal.cancel")}
        title={t("products:bulkActions.delete.modal.title")}
      />
    </>
  )
}
